import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tab } from '@headlessui/react';
import { Button } from '@/components/Common/button';

import * as yaml from 'js-yaml';

interface DomainMapping {
  id: string;
  domain: string;
  entityTypes: string[];
}

interface DomainEntityMappingFormProps {
  initialContent?: string;
  onSave: (content: string) => void;
  onCancel: () => void;
}

export function DomainEntityMappingForm({ initialContent, onSave, onCancel }: DomainEntityMappingFormProps) {
  const { t } = useTranslation();
  const [domainMappings, setDomainMappings] = useState<DomainMapping[]>([]);
  const [baseTypes, setBaseTypes] = useState<string[]>([]);
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());
  const [newEntityType, setNewEntityType] = useState<Record<string, string>>({});
  const [newBaseType, setNewBaseType] = useState('');

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = yaml.load(initialContent) as {
          domain_entity_mapping: Record<string, string[]>;
          base_types_always_included: string[];
        };

        // Parse domain mappings
        if (parsed?.domain_entity_mapping) {
          const mappings = Object.entries(parsed.domain_entity_mapping).map(([domain, types], index) => ({
            id: `domain-${Date.now()}-${index}`,
            domain,
            entityTypes: types || [],
          }));
          setDomainMappings(mappings);
        }

        // Parse base types
        if (parsed?.base_types_always_included && Array.isArray(parsed.base_types_always_included)) {
          setBaseTypes(parsed.base_types_always_included);
        }
      } catch (error) {
        console.error('Error parsing domain entity mapping YAML:', error);
      }
    }
  }, [initialContent]);

  const handleAddDomain = () => {
    const newDomain: DomainMapping = {
      id: `domain-${Date.now()}`,
      domain: '',
      entityTypes: [],
    };
    setDomainMappings([newDomain, ...domainMappings]);
    setExpandedDomains(new Set([...expandedDomains, newDomain.id]));
  };

  const handleUpdateDomain = (id: string, domain: string) => {
    setDomainMappings(domainMappings.map(dm =>
      dm.id === id ? { ...dm, domain } : dm
    ));
  };

  const handleDeleteDomain = (id: string) => {
    setDomainMappings(domainMappings.filter(dm => dm.id !== id));
    const newExpanded = new Set(expandedDomains);
    newExpanded.delete(id);
    setExpandedDomains(newExpanded);
  };

  const handleAddEntityType = (domainId: string) => {
    const typeToAdd = newEntityType[domainId]?.trim();
    if (!typeToAdd) return;

    setDomainMappings(domainMappings.map(dm =>
      dm.id === domainId
        ? { ...dm, entityTypes: [...dm.entityTypes, typeToAdd] }
        : dm
    ));
    setNewEntityType({ ...newEntityType, [domainId]: '' });
  };

  const handleDeleteEntityType = (domainId: string, typeIndex: number) => {
    setDomainMappings(domainMappings.map(dm =>
      dm.id === domainId
        ? { ...dm, entityTypes: dm.entityTypes.filter((_, i) => i !== typeIndex) }
        : dm
    ));
  };

  const handleAddBaseType = () => {
    const typeToAdd = newBaseType.trim();
    if (!typeToAdd) return;

    setBaseTypes([...baseTypes, typeToAdd]);
    setNewBaseType('');
  };

  const handleDeleteBaseType = (index: number) => {
    setBaseTypes(baseTypes.filter((_, i) => i !== index));
  };

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedDomains);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedDomains(newExpanded);
  };

  const handleSave = () => {
    // Convert domain mappings to object format
    const domainMappingObj: Record<string, string[]> = {};
    domainMappings.forEach(dm => {
      if (dm.domain) {
        domainMappingObj[dm.domain] = dm.entityTypes;
      }
    });

    const yamlContent = yaml.dump(
      {
        domain_entity_mapping: domainMappingObj,
        base_types_always_included: baseTypes,
      },
      {
        indent: 2,
        lineWidth: -1,
        noRefs: true,
        sortKeys: false,
      }
    );

    onSave(yamlContent);
  };

  return (
    <div className="space-y-4">
      <Tab.Group>
        <Tab.List className="flex space-x-1 rounded-xl bg-muted p-1">
          <Tab
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-all
              ${selected
                ? 'bg-background text-foreground shadow'
                : 'text-muted-foreground hover:bg-background/60 hover:text-foreground'
              }`
            }
          >
            {t('admin.configForms.domainEntityMapping.tabMapping')}
          </Tab>
          <Tab
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-all
              ${selected
                ? 'bg-background text-foreground shadow'
                : 'text-muted-foreground hover:bg-background/60 hover:text-foreground'
              }`
            }
          >
            {t('admin.configForms.domainEntityMapping.tabBaseTypes')}
          </Tab>
        </Tab.List>

        <Tab.Panels className="mt-4">
          {/* Domain Mappings Tab */}
          <Tab.Panel>
            <div className="space-y-4">
              <div className="flex justify-between items-center pb-4 border-b border-border">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t('admin.configForms.domainEntityMapping.intro')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('admin.configForms.domainEntityMapping.hint')}
                  </p>
                </div>
                <Button
                  onClick={handleAddDomain}
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  {t('admin.configForms.domainEntityMapping.addDomain')}
                </Button>
              </div>

              <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
                {domainMappings.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {t('admin.configForms.domainEntityMapping.empty')}
                  </div>
                ) : (
                  domainMappings.map((mapping) => (
                    <div
                      key={mapping.id}
                      className="border border-border rounded-lg overflow-hidden bg-card"
                    >
                      <div
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleExpanded(mapping.id)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                              {mapping.domain || 'new_domain'}
                            </code>
                            <span className="text-sm text-muted-foreground">
                              {t('admin.configForms.domainEntityMapping.typeCount', { count: mapping.entityTypes.length })}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteDomain(mapping.id);
                            }}
                            className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                          {expandedDomains.has(mapping.id) ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>

                      {expandedDomains.has(mapping.id) && (
                        <div className="p-4 pt-0 space-y-4 border-t border-border">
                          <div>
                            <label className="block text-sm font-medium mb-1">
                              {t('admin.configForms.domainEntityMapping.domainName')}
                              <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={mapping.domain}
                              onChange={(e) => handleUpdateDomain(mapping.id, e.target.value)}
                              placeholder={t('admin.configForms.domainEntityMapping.domainNamePlaceholder')}
                              className="w-full px-3 py-2 border border-border rounded-md bg-background font-mono text-sm"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              {t('admin.configForms.shared.snakeCaseHint')}
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium mb-2">
                              {t('admin.configForms.domainEntityMapping.entityTypes')}
                            </label>
                            <div className="space-y-2">
                              {mapping.entityTypes.map((type, index) => (
                                <div key={index} className="flex items-center gap-2">
                                  <code className="flex-1 px-3 py-2 bg-muted rounded-md text-sm font-mono">
                                    {type}
                                  </code>
                                  <button
                                    onClick={() => handleDeleteEntityType(mapping.id, index)}
                                    className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 rounded"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              ))}
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={newEntityType[mapping.id] || ''}
                                  onChange={(e) => setNewEntityType({ ...newEntityType, [mapping.id]: e.target.value })}
                                  onKeyPress={(e) => e.key === 'Enter' && handleAddEntityType(mapping.id)}
                                  placeholder="TransportRoute, Building..."
                                  className="flex-1 px-3 py-2 border border-border rounded-md bg-background text-sm font-mono"
                                />
                                <Button
                                  onClick={() => handleAddEntityType(mapping.id)}
                                  size="sm"
                                  variant="outline"
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </Tab.Panel>

          {/* Base Types Tab */}
          <Tab.Panel>
            <div className="space-y-4">
              <div className="pb-4 border-b border-border">
                <p className="text-sm text-muted-foreground">
                  {t('admin.configForms.domainEntityMapping.baseTypesIntro')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('admin.configForms.domainEntityMapping.baseTypesHint')}
                </p>
              </div>

              <div className="space-y-3">
                <div className="space-y-2">
                  {baseTypes.map((type, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 bg-muted rounded-md text-sm font-mono">
                        {type}
                      </code>
                      <button
                        onClick={() => handleDeleteBaseType(index)}
                        className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newBaseType}
                    onChange={(e) => setNewBaseType(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddBaseType()}
                    placeholder={t('admin.configForms.domainEntityMapping.baseTypePlaceholder')}
                    className="flex-1 px-3 py-2 border border-border rounded-md bg-background text-sm font-mono"
                  />
                  <Button
                    onClick={handleAddBaseType}
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    {t('global.add')}
                  </Button>
                </div>
              </div>
            </div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>

      <div className="flex gap-2 justify-end pt-4 border-t border-border">
        <Button onClick={onCancel} variant="outline">
          {t('actions.cancel')}
        </Button>
        <Button onClick={handleSave}>
          {t('actions.save')}
        </Button>
      </div>
    </div>
  );
}
